import React from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { Instagram, Mail, MapPin, Menu, X } from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";
import { SiteSettingsProvider, SeasonalBanner } from "./SiteFeatures";
import { StudioHours } from './StudioHours';

const NAV = [
  { to: "/", label: "Accueil" },
  { to: "/a-propos", label: "À propos" },
  { to: "/services", label: "Services" },
  { to: "/portfolio", label: "Portfolio" },
  { to: "/reserver-seance", label: "Réserver" },
  { to: "/bon-cadeau", label: "Bon cadeau" },
  { to: "/atelier", label: "L'Atelier Lumière" },
  { to: "/fiches-lumiere", label: "Fiches lumière" },
  { to: "/faq", label: "FAQ" },
  { to: "/contact", label: "Contact" },
];

export default function Layout({ children }) {
  const [open, setOpen] = React.useState(false);
  const loc = useLocation();
  React.useEffect(() => setOpen(false), [loc.pathname]);
  React.useEffect(() => {
    if (!open) return;
    const close = e => { if (e.key === 'Escape') { setOpen(false); document.querySelector('[data-testid="mobile-menu-toggle"]')?.focus(); } };
    window.addEventListener('keydown', close);
    return () => window.removeEventListener('keydown', close);
  }, [open]);

  // Scroll to hash anchor when hash changes (e.g. /#location-studio)
  React.useEffect(() => {
    if (loc.hash) {
      const id = loc.hash.replace("#", "");
      // small delay to let page render
      setTimeout(() => {
        const el = document.getElementById(id);
        if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
      }, 100);
    }
  }, [loc.pathname, loc.hash]);

  return (
    <SiteSettingsProvider><div className="min-h-screen flex flex-col bg-dark text-text-cream">
      <a href="#main-content" className="skip-link" data-testid="skip-to-content">Aller au contenu</a>
      <Header open={open} setOpen={setOpen} />
      <AnimatePresence>
        {open && <MobileMenu onClose={() => setOpen(false)} />}
      </AnimatePresence>
      <main id="main-content" tabIndex={-1} className="flex-1">{children}</main>
      <Footer />
    </div></SiteSettingsProvider>
  );
}

function Header({ open, setOpen }) {
  const [scrolled, setScrolled] = React.useState(false);
  React.useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 30);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  return (
    <header
      data-testid="site-header"
      className={`fixed top-0 left-0 right-0 z-50 bg-dark transition-colors duration-500 ${
        scrolled ? "border-b border-white/5" : ""
      }`}
    >
      <SeasonalBanner/>
      <div className="max-w-[1660px] mx-auto px-6 md:px-10 py-5 flex items-center justify-between gap-6">
        <Link to="/" data-testid="logo-link" className="flex items-center gap-3 leading-none">
          <img
            src="/logo-white.png"
            alt="Studio Euclides — Photographe"
            className="h-14 md:h-16 w-auto object-contain"
          />
          <span className="hidden sm:flex flex-col">
            <span className="font-serif text-xl md:text-2xl tracking-tight">
              Studio <span className="italic text-accent-light">Euclides</span>
            </span>
            <span className="eyebrow mt-1 text-[0.55rem]">Écublens — Suisse romande</span>
          </span>
        </Link>

        <nav aria-label="Navigation principale" className="hidden 2xl:flex items-center gap-4">
          {NAV.map((n) => (
            <NavLink
              key={n.to}
              to={n.to}
              data-testid={`nav-${n.to.replace(/\//g, "")}`}
              className={({ isActive }) =>
                `font-sans text-[0.70rem] uppercase tracking-[0.12em] whitespace-nowrap transition-colors ${
                  isActive ? "text-accent-light" : "text-text-cream/70 hover:text-accent-light"
                }`
              }
              end={n.to === "/"}
            >
              {n.label}
            </NavLink>
          ))}
        </nav>

        <button
          data-testid="mobile-menu-toggle"
          onClick={() => setOpen((s) => !s)}
          className="2xl:hidden text-text-cream"
          aria-label={open ? 'Fermer le menu' : 'Ouvrir le menu'}
          aria-expanded={open}
          aria-controls="mobile-navigation"
        >
          {open ? <X size={22} /> : <Menu size={22} />}
        </button>
      </div>
    </header>
  );
}

function MobileMenu({ onClose }) {
  return (
    <motion.div
      initial={{ x: "100%" }}
      animate={{ x: 0 }}
      exit={{ x: "100%" }}
      transition={{ duration: 0.4, ease: [0.2, 0.8, 0.2, 1] }}
      className="fixed inset-0 z-40 bg-dark 2xl:hidden flex flex-col pt-32 px-8 overflow-y-auto"
      data-testid="mobile-menu"
      id="mobile-navigation"
    >
      <nav aria-label="Navigation mobile" className="flex flex-col gap-4 mt-5 pb-8">
        {NAV.map((n) => (
          <Link
            key={n.to}
            to={n.to}
            data-testid={`mobile-nav-${n.to.replace(/\//g, "")}`}
            onClick={onClose}
            className="font-serif text-2xl sm:text-3xl text-text-cream hover:text-accent-light transition"
          >
            {n.label}
          </Link>
        ))}
      </nav>
      <div className="mt-auto mb-8 eyebrow text-text-cream/40">
        Route de Crochy 20 · 1024 Écublens
      </div>
    </motion.div>
  );
}

function Footer() {
  return (
    <footer className="bg-dark border-t border-white/5 px-6 md:px-12 pt-20 pb-10 relative overflow-hidden" data-testid="site-footer">
      <div className="absolute -bottom-32 -right-20 w-[480px] h-[480px] glow-amber-soft pointer-events-none" />
      <div className="max-w-[1480px] mx-auto grid md:grid-cols-4 gap-12 relative z-10">
        <div className="md:col-span-2">
          <div className="flex items-center gap-4">
            <img
              src="/logo-white.png"
              alt="Studio Euclides"
              className="h-14 w-auto opacity-95"
              width="56"
              height="56"
            />
            <div className="font-serif text-3xl">
              Studio <span className="italic text-accent-light">Euclides</span>
            </div>
          </div>
          <p className="mt-6 max-w-md text-text-cream/60 font-light leading-relaxed">
            Photographe portrait à Écublens. Des images qui restent, parce que chaque visage
            raconte une histoire qui mérite d'être capturée avec soin.
          </p>
          <div className="flex items-center gap-5 mt-6 text-text-cream/70">
            <a href="https://www.instagram.com/studio_euclides" target="_blank" rel="noopener noreferrer" aria-label="Instagram" data-testid="footer-instagram-icon" className="hover:text-accent-light">
              <Instagram size={18} strokeWidth={1.5} />
            </a>
            <a href="mailto:studioeuclides25@gmail.com" aria-label="Email" data-testid="footer-email-icon" className="hover:text-accent-light">
              <Mail size={18} strokeWidth={1.5} />
            </a>
          </div>
        </div>

        <div>
          <div className="eyebrow mb-5">Studio</div>
          <ul className="space-y-3 text-text-cream/70 font-light">
            <li className="flex items-start gap-2"><MapPin size={14} className="mt-1 text-accent-deep" strokeWidth={1.5}/> Route de Crochy 20<br/>1024 Écublens — Suisse</li>
            <li className="break-all"><a href="mailto:studioeuclides25@gmail.com" data-testid="footer-contact-email">studioeuclides25@gmail.com</a></li>
          </ul>
          <StudioHours id="footer" compact/>
        </div>

        <div>
          <div className="eyebrow mb-5">Navigation</div>
          <ul className="space-y-3 text-text-cream/70 font-light">
            <li><Link to="/reserver-seance" data-testid="footer-book-session" className="hover:text-accent-light">Réserver une séance</Link></li>
            <li><Link to="/bon-cadeau" data-testid="footer-gift-card" className="hover:text-accent-light">Bon cadeau</Link></li>
            <li><Link to="/fiches-lumiere" data-testid="footer-light-sheets" className="hover:text-accent-light">Fiches lumière</Link></li>
            <li><Link to="/reservation" data-testid="footer-rental" className="hover:text-accent-light">Location du studio</Link></li>
            <li><Link to="/faq" data-testid="footer-faq" className="hover:text-accent-light">FAQ</Link></li>
            <li><Link to="/contact" data-testid="footer-contact" className="hover:text-accent-light">Contact</Link></li>
            <li><a href="https://www.instagram.com/studio_euclides" target="_blank" rel="noopener noreferrer" data-testid="footer-instagram">Instagram @studio_euclides</a></li>
            <li><Link to="/services" className="hover:text-accent-light">Services</Link></li>
            <li><Link to="/portfolio" className="hover:text-accent-light">Portfolio</Link></li>
            <li><Link to="/atelier" className="hover:text-accent-light">L'Atelier Lumière</Link></li>
            <li><Link to="/mentions-legales" className="hover:text-accent-light" data-testid="footer-legal">Mentions légales</Link></li>
            <li><Link to="/conditions-generales" className="hover:text-accent-light" data-testid="footer-terms">Conditions générales</Link></li>
            <li><Link to="/politique-confidentialite" className="hover:text-accent-light">Confidentialité</Link></li>
          </ul>
        </div>
      </div>
      <div className="max-w-[1480px] mx-auto mt-16 pt-6 border-t border-white/5 flex flex-col md:flex-row justify-between gap-3 text-text-cream/40 text-xs">
        <span>© {new Date().getFullYear()} Studio Euclides — Tous droits réservés.</span>
        <span className="eyebrow text-[0.6rem]">Crafted with intention.</span>
      </div>
    </footer>
  );
}
